// src/components/contacts/ContactDetail.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Card, CardContent, Typography, Chip, Tabs, Tab,
  Divider, IconButton, List, ListItem, ListItemText, ListItemIcon,
  Paper, Link, useTheme, useMediaQuery, ListItemButton,
} from "@mui/material";
import {
  ArrowBack, Email, Phone, LocationOn, Business, Person,
  Receipt, Assignment, Smartphone, Badge, Work, Language,
  Visibility, Euro, CalendarToday,
} from "@mui/icons-material";
import { useDocument } from "../../hooks/useDocument";
import { useCollection } from "../../hooks/useCollection";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import {
  CONTACT_TYPES,
  CONTACT_TYPE_LABELS,
  COMPANY_ONLY_TYPES,
} from "../../utils/models/contactModel";
import { CLIENT_TYPES } from "../../utils/models/clientModel";
import { PROJECT_STATUS_LABELS } from "../../utils/models/projectModel";

const ContactDetail = ({ contactId, onEdit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { document: contact, loading } = useDocument("contacts", contactId);
  const { data: invoices } = useCollection("invoices");
  const { data: projects } = useCollection("projects");
  const [activeTab, setActiveTab] = useState(0);

  if (loading) return <LoadingSpinner />;
  if (!contact) return <EmptyState title="Contacto no encontrado" />;

  const isCompanyOnly = COMPANY_ONLY_TYPES.includes(contact.contactType);
  const isPersonType =
    contact.contactType === CONTACT_TYPES.EPC ||
    contact.contactType === CONTACT_TYPES.COMMERCIAL ||
    (contact.contactType === CONTACT_TYPES.CLIENT &&
      contact.clientType === CLIENT_TYPES.INDIVIDUAL);

  const isClientCompany =
    contact.contactType === CONTACT_TYPES.CLIENT &&
    contact.clientType === CLIENT_TYPES.COMPANY;

  const isSupplier = contact.contactType === CONTACT_TYPES.SUPPLIER;

  const displayName = isCompanyOnly
    ? contact.companyName
    : isClientCompany
      ? contact.companyName
      : `${contact.firstName || ""} ${contact.lastName || ""}`.trim();

  // Filtrar facturas y proyectos
  const contactInvoices = invoices?.filter((i) => 
    i.contactId === contactId && i.isActive !== false
  ) || [];
  
  const contactProjects = projects?.filter((p) => 
    (p.clientId === contact.clientId || p.clientId === contactId) && p.isActive !== false
  ) || [];

  const sortedInvoices = [...contactInvoices].sort((a, b) => 
    new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0)
  );

  const sortedProjects = [...contactProjects].sort((a, b) => 
    new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  const hasAddress = contact.address?.street || contact.address?.city;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "—";
    return `${Number(amount).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
  };

  const getInvoiceStatusLabel = (status) => {
    const labels = { draft: "Borrador", sent: "Enviada", paid: "Pagada", overdue: "Vencida", cancelled: "Cancelada" };
    return labels[status] || status || "—";
  };

  const getInvoiceStatusColor = (status) => {
    const colors = { draft: "default", sent: "info", paid: "success", overdue: "error", cancelled: "warning" };
    return colors[status] || "default";
  };

  const getTypeColor = (type) => {
    switch (type) {
      case CONTACT_TYPES.CLIENT: return "primary";
      case CONTACT_TYPES.SUPPLIER: return "warning";
      case CONTACT_TYPES.EPC: return "success";
      case CONTACT_TYPES.COMMERCIAL: return "info";
      default: return "default";
    }
  };

  const getTypeIcon = (type) => {
    if (COMPANY_ONLY_TYPES.includes(type) || isClientCompany) return <Business />;
    return <Person />;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: '1px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0, 95, 101, 0.12)',
        background: theme.palette.background.paper,
        overflow: "hidden",
      }}
    >
      {/* CABECERA */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1, sm: 2 }, mb: { xs: 3, sm: 4 }, flexWrap: "wrap" }}>
        <IconButton onClick={() => navigate("/contacts")} size="small" sx={{ background: theme.palette.background.paper, border: '1px solid', borderColor: theme.palette.divider, mt: 0.5 }}>
          <ArrowBack sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" }, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', wordBreak: "break-word" }}>
            {displayName || "Sin nombre"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            <Chip icon={getTypeIcon(contact.contactType)} label={CONTACT_TYPE_LABELS[contact.contactType] || contact.contactType} size="small" color={getTypeColor(contact.contactType)} variant="outlined" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }} />
            {isClientCompany && <Chip icon={<Business sx={{ fontSize: { xs: 14, sm: 16 } }} />} label="Empresa" size="small" color="secondary" variant="outlined" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }} />}
            {contact.companyName && isPersonType && <Chip icon={<Business sx={{ fontSize: { xs: 14, sm: 16 } }} />} label={contact.companyName} size="small" variant="outlined" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }} />}
          </Box>
        </Box>
      </Box>

      {/* CONTENIDO PRINCIPAL */}
      <Box sx={{ display: "flex", gap: { xs: 2, sm: 3 }, flexWrap: "wrap", width: "100%", flexDirection: { xs: "column", md: "row" } }}>
        {/* INFORMACIÓN - LADO IZQUIERDO */}
        <Box sx={{ flex: { md: "1 1 340px" }, width: { xs: "100%", md: "auto" }, minWidth: { md: "300px" } }}>
          <Card elevation={0} sx={{ borderRadius: "16px", border: '1px solid', borderColor: theme.palette.divider, background: theme.palette.background.paper, height: "100%" }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: theme.palette.primary.main, borderBottom: '2px solid', borderColor: theme.palette.primary.main, pb: 1, display: 'inline-block', fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                Información de contacto
              </Typography>

              <List dense disablePadding>
                {contact.email && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><Email sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.primary.main }} /></ListItemIcon>
                    <ListItemText primary="Email" secondary={contact.email} primaryTypographyProps={{ variant: "caption", color: "text.secondary", fontSize: { xs: "0.7rem", sm: "0.75rem" } }} secondaryTypographyProps={{ variant: "body2", fontSize: { xs: "0.8rem", sm: "0.85rem" }, wordBreak: "break-all" }} />
                  </ListItem>
                )}
                {contact.phone && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><Phone sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.success.main }} /></ListItemIcon>
                    <ListItemText primary="Teléfono" secondary={contact.phone} primaryTypographyProps={{ variant: "caption", color: "text.secondary", fontSize: { xs: "0.7rem", sm: "0.75rem" } }} secondaryTypographyProps={{ variant: "body2", fontSize: { xs: "0.8rem", sm: "0.85rem" } }} />
                  </ListItem>
                )}
                {contact.mobile && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><Smartphone sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.info.main }} /></ListItemIcon>
                    <ListItemText primary="Móvil" secondary={contact.mobile} primaryTypographyProps={{ variant: "caption", color: "text.secondary", fontSize: { xs: "0.7rem", sm: "0.75rem" } }} secondaryTypographyProps={{ variant: "body2", fontSize: { xs: "0.8rem", sm: "0.85rem" } }} />
                  </ListItem>
                )}
                {hasAddress && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><LocationOn sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.error.main }} /></ListItemIcon>
                    <ListItemText primary="Dirección" secondary={`${contact.address.street || ""}, ${contact.address.city || ""}, ${contact.address.state || ""} ${contact.address.zipCode || ""}`} primaryTypographyProps={{ variant: "caption", color: "text.secondary", fontSize: { xs: "0.7rem", sm: "0.75rem" } }} secondaryTypographyProps={{ variant: "body2", fontSize: { xs: "0.8rem", sm: "0.85rem" } }} />
                  </ListItem>
                )}
              </List>

              {/* ENLACES A ENTIDADES RELACIONADAS */}
              {(contact.clientId || contact.supplierId || contact.commercialId) && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      mb: 1,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    }}
                  >
                    Entidades relacionadas
                  </Typography>
                  <List dense disablePadding>
                    {contact.clientId && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Person sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText>
                          <Link
                            component="button"
                            onClick={() => navigate(`/clients/${contact.clientId}`)}
                            underline="hover"
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              fontSize: { xs: "0.8rem", sm: "0.85rem" },
                              cursor: "pointer",
                            }}
                          >
                            Ver cliente →
                          </Link>
                        </ListItemText>
                      </ListItem>
                    )}
                    {contact.supplierId && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Business sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.warning.main }} />
                        </ListItemIcon>
                        <ListItemText>
                          <Link
                            component="button"
                            onClick={() => navigate(`/suppliers/${contact.supplierId}`)}
                            underline="hover"
                            sx={{
                              color: theme.palette.warning.main,
                              fontWeight: 500,
                              fontSize: { xs: "0.8rem", sm: "0.85rem" },
                              cursor: "pointer",
                            }}
                          >
                            Ver proveedor →
                          </Link>
                        </ListItemText>
                      </ListItem>
                    )}
                    {contact.commercialId && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Badge sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.info.main }} />
                        </ListItemIcon>
                        <ListItemText>
                          <Link
                            component="button"
                            onClick={() => navigate(`/commercials/${contact.commercialId}`)}
                            underline="hover"
                            sx={{
                              color: theme.palette.info.main,
                              fontWeight: 500,
                              fontSize: { xs: "0.8rem", sm: "0.85rem" },
                              cursor: "pointer",
                            }}
                          >
                            Ver comercial →
                          </Link>
                        </ListItemText>
                      </ListItem>
                    )}
                  </List>
                </>
              )}

              {contact.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>Notas</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contact.notes}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* FACTURAS Y PROYECTOS - LADO DERECHO */}
        <Box sx={{ flex: { md: "2 1 540px" }, width: { xs: "100%", md: "auto" }, minWidth: { md: "350px" } }}>
          <Card elevation={0} sx={{ borderRadius: "16px", border: '1px solid', borderColor: theme.palette.divider, background: theme.palette.background.paper, overflow: "hidden" }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ px: { xs: 1, sm: 2 }, pt: 2, borderBottom: "1px solid", borderColor: theme.palette.divider, '& .MuiTab-root': { color: theme.palette.text.secondary, fontSize: { xs: "0.75rem", sm: "0.85rem" }, minWidth: { xs: "auto", sm: 90 }, px: { xs: 1, sm: 2 }, '&.Mui-selected': { color: theme.palette.primary.main } }, '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main } }}>
              <Tab label={`Facturas (${contactInvoices.length})`} icon={<Receipt sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" />
              <Tab label={`Proyectos (${contactProjects.length})`} icon={<Assignment sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" />
            </Tabs>

            {/* LISTA DE FACTURAS */}
            {activeTab === 0 && (
              sortedInvoices.length === 0 ? (
                <Box sx={{ p: 3 }}><EmptyState title="Sin facturas" description="Este contacto no tiene facturas aún" compact /></Box>
              ) : (
                <List dense disablePadding>
                  {sortedInvoices.map((invoice) => (
                    <ListItem key={invoice.id} disablePadding sx={{ borderBottom: '1px solid', borderColor: theme.palette.divider }}>
                      <ListItemButton onClick={() => navigate(`/invoices/${invoice.id}`)} sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                        <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}><Receipt sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.primary.main }} /></ListItemIcon>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>{invoice.invoiceNumber || `Factura #${invoice.id?.substring(0, 8)}`}</Typography>}
                          secondary={<Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.5 }}><Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" }, display: "flex", alignItems: "center", gap: 0.5 }}><CalendarToday sx={{ fontSize: 12 }} />{formatDate(invoice.date || invoice.createdAt)}</Typography><Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" }, display: "flex", alignItems: "center", gap: 0.5 }}><Euro sx={{ fontSize: 12 }} />{formatCurrency(invoice.total)}</Typography></Box>}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip label={getInvoiceStatusLabel(invoice.status)} size="small" color={getInvoiceStatusColor(invoice.status)} sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" }, height: { xs: 22, sm: 24 } }} />
                          <Visibility sx={{ fontSize: { xs: 16, sm: 20 }, color: theme.palette.text.secondary }} />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )
            )}

            {/* LISTA DE PROYECTOS */}
            {activeTab === 1 && (
              sortedProjects.length === 0 ? (
                <Box sx={{ p: 3 }}><EmptyState title="Sin proyectos" description="Este contacto no tiene proyectos aún" compact /></Box>
              ) : (
                <List dense disablePadding>
                  {sortedProjects.map((project) => (
                    <ListItem key={project.id} disablePadding sx={{ borderBottom: '1px solid', borderColor: theme.palette.divider }}>
                      <ListItemButton onClick={() => navigate(`/projects/${project.id}`)} sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                        <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}><Assignment sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.secondary.main }} /></ListItemIcon>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>{project.name || "Sin nombre"}</Typography>}
                          secondary={<Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.5 }}><Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>{project.address?.street ? `${project.address.street}, ${project.address.city || ""}` : "Sin dirección"}</Typography></Box>}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip label={PROJECT_STATUS_LABELS[project.status] || project.status || "—"} size="small" color={project.status === "completed" ? "success" : project.status === "in_progress" ? "primary" : "default"} variant="outlined" sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" }, height: { xs: 22, sm: 24 } }} />
                          <Visibility sx={{ fontSize: { xs: 16, sm: 20 }, color: theme.palette.text.secondary }} />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )
            )}
          </Card>
        </Box>
      </Box>
    </Paper>
  );
};

export default ContactDetail;